package com.arena_web.ui.components.feed

import androidx.compose.runtime.*
import com.arena_web.data.dto.DeletedAccount
import com.arena_web.data.dto.FollowUserDto
import com.arena_web.data.dto.isDeletedAccount
import com.arena_web.domain.repositories.FollowRepository
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.icons.TintedGlyph
import com.arena_web.ui.components.overlay.FollowConfirmDialog
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.theme.SportColorScheme
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.TextInput
import org.w3c.dom.events.Event

// Figma 8702:137732 — sidebar surface tokens.
private const val SIDEBAR_BG =
    "linear-gradient(171.5deg, rgba(60, 121, 241, 0.135) 14%, rgba(5, 4, 12, 0.03) 70%)"
private val ROW_BUTTON_BG =
    AppGradients.InteractiveSecondary
// Selected-row treatment — mirrors ChatListItem (Figma chat list):
// solid dark fill inside, blue gradient on the left edge via the
// padding-box/border-box layering trick so a blue accent bar appears on
// the left of the selected row.
private const val ROW_SELECTED_BG =
    "linear-gradient(var(--l-hover2, #091a40), var(--l-hover2, #091a40)) padding-box, " +
        "linear-gradient(90deg, #0061FF, #3C79F100 5%) border-box"

/**
 * Right-rail sidebar shown on the Feed screen — Figma 8702:137732.
 *
 * The outer card has a 22px top-corner radius with left / right / top borders
 * only (the bottom edge bleeds off the visible area) and a subtle blue-tinted
 * surface gradient. Inside, two stacked sections list who I follow and who
 * follows me, each with its own pill action on every row.
 *
 *   • Following row → filled-blue "Following" pill that flips to outlined
 *     "Follow" once the viewer unfollows in-session.
 *   • Follower row  → outlined "Remove" pill that flips to a disabled
 *     "Deleted" pill once the viewer removes that follower.
 */
private const val FOLLOW_SIDEBAR_SCROLL_ID = "feed-follow-sidebar-scroll"
private const val FOLLOW_PAGE_SIZE = 20
private const val FOLLOW_SEARCH_DEBOUNCE_MS = 300L

// Extra left indent for the Following / Followers sections so they sit
// further from the sidebar edge than the search input above them.
private const val SECTION_LEFT_INDENT = 12

@Composable
fun FeedFollowSidebar(
    followRepository: FollowRepository,
    currentUserId: String?,
    initialFollowing: List<FollowUserDto>,
    initialFollowingTotal: Long,
    initialFollowingHasMore: Boolean,
    initialFollowers: List<FollowUserDto>,
    initialFollowersTotal: Long,
    initialFollowersHasMore: Boolean,
    onUserClick: (FollowUserDto) -> Unit,
    selectedUserId: String? = null,
    onDiscoverPlayersClick: () -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val t = LocalAppStrings.current
    val scope = rememberCoroutineScope()

    // Initial page data is fetched by the parent page so the screen-wide
    // loader can wait on all APIs at once; here we just hold + extend it.
    var following by remember { mutableStateOf(initialFollowing) }
    var followers by remember { mutableStateOf(initialFollowers) }
    var followingPage by remember { mutableStateOf(1) }
    var followersPage by remember { mutableStateOf(1) }
    var followingHasMore by remember { mutableStateOf(initialFollowingHasMore) }
    var followersHasMore by remember { mutableStateOf(initialFollowersHasMore) }
    var followingTotal by remember { mutableStateOf(initialFollowingTotal) }
    var followersTotal by remember { mutableStateOf(initialFollowersTotal) }
    var isLoadingMoreFollowing by remember { mutableStateOf(false) }
    var isLoadingMoreFollowers by remember { mutableStateOf(false) }
    var searchQuery by remember { mutableStateOf("") }

    // Per-row session toggles — keep the row in place after a click instead
    // of dropping it from the list, so the user can undo without a refetch.
    val unfollowedIds = remember { mutableStateMapOf<String, Boolean>() }
    val removedFollowerIds = remember { mutableStateMapOf<String, Boolean>() }

    // Pending confirmation. Destructive actions (unfollow, remove follower)
    // route through this so the user has to acknowledge before the API call
    // fires. Re-follow is not destructive so it skips the dialog.
    var pendingAction by remember { mutableStateOf<PendingAction?>(null) }

    // Track whether the on-screen lists came from an explicit user search vs
    // the initial server-rendered page. While `appliedSearch` is non-blank
    // the empty-states swap to "No results", and re-fetch when the input
    // returns to empty so the original lists slide back in.
    var appliedSearch by remember { mutableStateOf("") }
    var isSearching by remember { mutableStateOf(false) }

    fun pageParams(page: Int, search: String): Map<String, String> {
        val base = mapOf("page" to "$page", "size" to "$FOLLOW_PAGE_SIZE")
        return if (search.isBlank()) base else base + ("search" to search.trim())
    }

    // Debounced server search. When the input changes we wait ~300ms for the
    // user to settle, then reset both sections to page 1 with the new query.
    LaunchedEffect(searchQuery) {
        // First composition with the seeded query — nothing to do; the
        // parent already supplied the initial lists.
        if (searchQuery == appliedSearch) return@LaunchedEffect
        delay(FOLLOW_SEARCH_DEBOUNCE_MS)
        val q = searchQuery
        appliedSearch = q
        isSearching = true
        val followingResult = followRepository.getMyFollowing(pageParams(1, q))
        val followersResult = followRepository.getMyFollowers(pageParams(1, q))
        followingResult.onSuccess { page ->
            following = page.items
            followingPage = page.page
            followingHasMore = page.page < page.totalPages
            followingTotal = page.totalItems
        }
        followersResult.onSuccess { page ->
            followers = page.items
            followersPage = page.page
            followersHasMore = page.page < page.totalPages
            followersTotal = page.totalItems
        }
        isSearching = false
    }

    // Load next page handlers — prefer the section closest to the scroll
    // bottom (Followers). Once that's exhausted, fall back to expanding the
    // Following section (which sits above but still needs paging). All
    // requests now carry the active search query, so paging stays consistent
    // with what the input shows.
    val loadMore: () -> Unit = {
        when {
            followersHasMore && !isLoadingMoreFollowers -> {
                isLoadingMoreFollowers = true
                scope.launch {
                    val next = followersPage + 1
                    followRepository.getMyFollowers(pageParams(next, appliedSearch)).onSuccess { page ->
                        followers = followers + page.items
                        followersPage = next
                        followersHasMore = page.items.size >= FOLLOW_PAGE_SIZE && page.page < page.totalPages
                        followersTotal = page.totalItems
                    }
                    isLoadingMoreFollowers = false
                }
            }
            followingHasMore && !isLoadingMoreFollowing -> {
                isLoadingMoreFollowing = true
                scope.launch {
                    val next = followingPage + 1
                    followRepository.getMyFollowing(pageParams(next, appliedSearch)).onSuccess { page ->
                        following = following + page.items
                        followingPage = next
                        followingHasMore = page.items.size >= FOLLOW_PAGE_SIZE && page.page < page.totalPages
                        followingTotal = page.totalItems
                    }
                    isLoadingMoreFollowing = false
                }
            }
        }
    }

    DisposableEffect(Unit) {
        val handler: (Event) -> Unit = {
            val el = document.getElementById(FOLLOW_SIDEBAR_SCROLL_ID)
            if (el != null) {
                val scrollTop = el.scrollTop
                val scrollHeight = el.scrollHeight
                val clientHeight = el.clientHeight
                if (scrollHeight - scrollTop - clientHeight < 200) {
                    loadMore()
                }
            }
        }
        val el = document.getElementById(FOLLOW_SIDEBAR_SCROLL_ID)
        el?.addEventListener("scroll", handler)
        onDispose { el?.removeEventListener("scroll", handler) }
    }

    // Lists already reflect the server-applied search filter; no further
    // client-side narrowing needed. We do still gate the empty-state copy on
    // the active search so "No results" only shows during an active query.
    val filteredFollowing = following
    val filteredFollowers = followers
    val isSearchActive = appliedSearch.isNotBlank() || searchQuery.isNotBlank()

    // Surface card — tinted blue gradient + backdrop blur, rounded top
    // corners. Border removed per design tweak; the bottom edge bleeds off
    // the viewport. Inner column owns the scroll so long lists don't push
    // the header off-screen.
    Box(
        modifier = modifier.styleModifier {
            property("background", SIDEBAR_BG)
            property("border-top-left-radius", s(22))
            property("border-top-right-radius", s(22))
            property("backdrop-filter", "blur(11px)")
            property("-webkit-backdrop-filter", "blur(11px)")
            property("overflow", "hidden")
        }
    ) {
        Column(
            modifier = Modifier.fillMaxSize().id(FOLLOW_SIDEBAR_SCROLL_ID).styleModifier {
                property("gap", s(22))
                property("padding", "${s(22)} ${s(16)} ${s(16)} ${s(16)}")
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                property("scrollbar-width", "thin")
                property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
            }
        ) {
            // Shared search input — sits above both sections and filters
            // Following + Followers simultaneously.
            SearchInput(value = searchQuery, onValueChange = { searchQuery = it })

            // ── Following section ─────────────────────────────────────────
            SectionHeader(title = t.feedFollowingSection, count = followingTotal)

            when {
                isSearching && following.isEmpty() -> EmptyState(t.feedSearching)
                following.isEmpty() && !isSearchActive -> FollowingEmptyState(
                    onDiscoverClick = onDiscoverPlayersClick,
                )
                filteredFollowing.isEmpty() -> EmptyState(t.feedNoResults)
                else -> filteredFollowing.forEach { user ->
                    val stillFollowing = unfollowedIds[user.userId] != true
                    ProfileRow(
                        user = user,
                        selected = user.userId == selectedUserId,
                        onClick = { onUserClick(user) },
                    ) {
                        FollowingPill(
                            stillFollowing = stillFollowing,
                            onToggle = {
                                if (stillFollowing) {
                                    // Destructive — gate behind confirm dialog.
                                    pendingAction = PendingAction.Unfollow(user)
                                } else {
                                    // Re-follow is reversible / non-destructive,
                                    // execute immediately.
                                    unfollowedIds[user.userId] = false
                                    scope.launch {
                                        followRepository.followUser(user.userId)
                                            .onFailure { unfollowedIds[user.userId] = true }
                                    }
                                }
                            },
                        )
                    }
                }
            }

            // ── Followers section ─────────────────────────────────────────
            SectionHeader(title = t.feedFollowersSection, count = followersTotal)

            when {
                isSearching && followers.isEmpty() -> EmptyState(t.feedSearching)
                followers.isEmpty() && !isSearchActive -> FollowersEmptyState()
                filteredFollowers.isEmpty() -> EmptyState(t.feedNoResults)
                else -> filteredFollowers.forEach { user ->
                    val deleted = removedFollowerIds[user.userId] == true
                    ProfileRow(
                        user = user,
                        selected = user.userId == selectedUserId,
                        onClick = { onUserClick(user) },
                    ) {
                        RemovePill(
                            deleted = deleted,
                            onRemove = {
                                pendingAction = PendingAction.RemoveFollower(user)
                            },
                        )
                    }
                }
            }
            if (isLoadingMoreFollowers || isLoadingMoreFollowing) {
                EmptyState(t.feedLoadingMore)
            }
        }
    }

    // Confirmation dialog — anchored at the sidebar level so its fixed
    // overlay isn't clipped by the card's `overflow: hidden`.
    when (val action = pendingAction) {
        is PendingAction.Unfollow -> {
            val name = action.user.fullName?.takeIf { it.isNotBlank() }
                ?: action.user.username.takeIf { !it.contains('@') }
                ?: t.feedThisUser
            FollowConfirmDialog(
                user = action.user,
                title = t.feedUnfollowTitle(name),
                subtitle = t.feedUnfollowSubtitle,
                confirmLabel = t.feedUnfollow,
                onConfirm = {
                    unfollowedIds[action.user.userId] = true
                    scope.launch {
                        followRepository.unfollowUser(action.user.userId)
                            .onFailure { unfollowedIds[action.user.userId] = false }
                    }
                    pendingAction = null
                },
                onCancel = { pendingAction = null },
            )
        }
        is PendingAction.RemoveFollower -> {
            val name = action.user.fullName?.takeIf { it.isNotBlank() }
                ?: action.user.username.takeIf { !it.contains('@') }
                ?: t.feedThisUser
            FollowConfirmDialog(
                user = action.user,
                title = t.feedRemoveTitle(name),
                subtitle = t.feedRemoveSubtitle,
                confirmLabel = t.feedRemove,
                onConfirm = {
                    removedFollowerIds[action.user.userId] = true
                    scope.launch {
                        followRepository.removeFollower(action.user.userId)
                            .onFailure { removedFollowerIds[action.user.userId] = false }
                    }
                    pendingAction = null
                },
                onCancel = { pendingAction = null },
            )
        }
        null -> Unit
    }
}

/** Discriminator for the destructive flow that needs a confirm step. */
private sealed class PendingAction {
    data class Unfollow(val user: FollowUserDto) : PendingAction()
    data class RemoveFollower(val user: FollowUserDto) : PendingAction()
}

// =============================================================================
// Section header — "Following 5" / "Followers 8"
// =============================================================================

@Composable
private fun SectionHeader(title: String, count: Long) {
    // Per Figma, the header reserves 12px of padding-bottom for a future
    // underline but the border itself is transparent (rgba(148,149,152,0))
    // so we keep the spacing without drawing anything.
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPaddingBottom(12)
            .sGap(8)
            .styleModifier { property("padding-left", s(SECTION_LEFT_INDENT)) },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            title,
            modifier = Modifier
                .sFontSize(20)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(26)
                .sLetterSpacing(0.2)
                .color(com.arena_web.ui.theme.ThemeVars.Text2),
        )
        CountBadge(count)
    }
}

@Composable
private fun CountBadge(count: Long) {
    val sport = LocalSportColors.current
    Box(
        modifier = Modifier
            .sSize(20)
            .sBorderRadius(100)
            .backgroundColor(sport.primary)
            .overflow(Overflow.Hidden),
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            "$count",
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.SemiBold)
                .sLineHeight(14)
                .color(sport.onPrimary)
                .styleModifier { property("transform", "translateY(1px)") },
        )
    }
}

// =============================================================================
// Search input — 44px tall pill, gradient border (Figma 8702:137740 / 5382:11809)
// =============================================================================

/** Mask-composite gradient border overlay. Parent must be `position: relative`. */
@Composable
private fun GradientBorder(radiusPx: Int) {
    Box(
        modifier = Modifier.fillMaxSize().styleModifier {
            property("position", "absolute")
            property("inset", "0")
            property("border", "0.3px solid transparent")
            property("border-radius", s(radiusPx))
            property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
            property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
            property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
            property("-webkit-mask-composite", "xor")
            property("mask-composite", "exclude")
            property("pointer-events", "none")
        },
    )
}

@Composable
private fun SearchInput(value: String, onValueChange: (String) -> Unit) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(44)
            .styleModifier {
                property("position", "relative")
                property("border-radius", s(28))
                property("background", "transparent")
            },
    ) {
        GradientBorder(28)
        Row(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("padding-left", s(12))
                    property("padding-right", s(8))
                    property("gap", s(6))
                },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(
                src = AppIcons.Search,
                modifier = Modifier.sSize(24).styleModifier { property("opacity", "0.5") },
            )
            TextInput(
                value = value,
                attrs = Modifier
                    .fillMaxWidth()
                    .sFontSize(16)
                    .fontWeight(AppTypography.Regular)
                    .color(com.arena_web.ui.theme.ThemeVars.Text1)
                    .styleModifier {
                        property("background", "transparent")
                        property("border", "none")
                        property("outline", "none")
                        property("font-family", "Urbanist, sans-serif")
                        property("letter-spacing", "0.16px")
                    }
                    .toAttrs {
                        attr("placeholder", t.commonSearch)
                        onInput { e -> onValueChange(e.target.asDynamic().value as String) }
                        style { property("color", "white") }
                    },
            )
        }
    }
}

// =============================================================================
// Profile row — 40×60 player-card avatar + username/full name + action pill
// =============================================================================

@Composable
private fun ProfileRow(
    user: FollowUserDto,
    selected: Boolean = false,
    onClick: () -> Unit,
    action: @Composable () -> Unit,
) {
    // Border + paddings are present in BOTH states (transparent border when
    // not selected) so the row never resizes / jumps on toggle. Same trick
    // ChatListItem uses for its selected state.
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("border", "3px solid transparent")
                property("border-radius", s(14))
                property("transition", "background 0.15s ease")
                property("margin-bottom", s(10))
                property("margin-left", s(SECTION_LEFT_INDENT))
                if (selected) {
                    property("padding", "${s(8)} ${s(12)} ${s(8)} ${s(10)}")
                    property("background", ROW_SELECTED_BG)
                } else {
                    property("padding", "${s(8)} ${s(12)} ${s(8)} ${s(8)}")
                    property("background", "transparent")
                }
            },
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(
            modifier = Modifier
                .sGap(8)
                .cursor(Cursor.Pointer)
                .onClick { onClick() }
                .flexGrow(1)
                .styleModifier { property("min-width", "0") },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            UserCardAvatar(user)
            // Human name on top, optional city as a secondary line below —
            // username intentionally omitted (matches FollowListDialog).
            // Falls back to the @handle (or "Deleted Account" for
            // anonymized users) when no full name is set so the row
            // never renders empty.
            val primaryLine = when {
                user.isDeletedAccount() -> DeletedAccount.DISPLAY_NAME
                !user.fullName.isNullOrBlank() -> user.fullName
                else -> user.username
            }
            val cityLine = user.city
                ?.takeIf { it.isNotBlank() && !user.isDeletedAccount() }
            Column(
                modifier = Modifier.styleModifier {
                    property("gap", s(2))
                    property("min-width", "0")
                },
            ) {
                SpanText(
                    primaryLine,
                    modifier = Modifier
                        .sFontSize(18)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(22)
                        .sLetterSpacing(0.09)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .whiteSpace(WhiteSpace.NoWrap)
                        .overflow(Overflow.Hidden)
                        .textOverflow(TextOverflow.Ellipsis),
                )
                if (cityLine != null) {
                    SpanText(
                        cityLine,
                        modifier = Modifier
                            .sFontSize(14)
                            .fontWeight(AppTypography.Regular)
                            .sLineHeight(20)
                            .sLetterSpacing(0.14)
                            .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                            .whiteSpace(WhiteSpace.NoWrap)
                            .overflow(Overflow.Hidden)
                            .textOverflow(TextOverflow.Ellipsis),
                    )
                }
            }
        }
        action()
    }
}

/**
 * 40×60 player-card variant for the sidebar — falls back to a simple square
 * avatar when the user hasn't generated a card yet. We use [PlayerCardSize.Highlight]
 * (37×58) since it's the closest enum value to the 40×60 Figma spec; the
 * difference is small enough to read as the same component.
 */
@Composable
private fun UserCardAvatar(user: FollowUserDto) {
    val cardFrameUrl = user.cardImageUrl
    Box(
        modifier = Modifier
            .styleModifier {
                property("flex-shrink", "0")
                property("width", s(40))
                property("height", s(60))
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            },
    ) {
        if (cardFrameUrl != null) {
            PlayerCard(
                playerName = "",
                rating = 0,
                position = "",
                sport = "football",
                size = PlayerCardSize.Highlight,
                cardFrameUrl = cardFrameUrl,
                avatarUrl = user.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                hideRating = true,
                hideShadow = true,
            )
        } else {
            // Fallback — rounded avatar tile when no card has been generated.
            val avatarUrl = user.avatarUrl
            Box(
                modifier = Modifier
                    .sWidth(40)
                    .sHeight(60)
                    .styleModifier {
                        property("border-radius", s(6))
                        property("background", "rgba(60, 121, 241, 0.15)")
                        property("overflow", "hidden")
                    },
                contentAlignment = Alignment.Center,
            ) {
                if (avatarUrl != null) {
                    Div(attrs = {
                        ref { el ->
                            el.innerHTML =
                                "<img src=\"$avatarUrl\" referrerpolicy=\"no-referrer\" " +
                                    "style=\"width:100%;height:100%;object-fit:cover;display:block;\" />"
                            onDispose { }
                        }
                        style {
                            property("width", "100%")
                            property("height", "100%")
                        }
                    })
                } else {
                    SpanText(
                        user.username.take(1).uppercase(),
                        modifier = Modifier
                            .sFontSize(16)
                            .fontWeight(AppTypography.SemiBold)
                            .color(com.arena_web.ui.theme.ThemeVars.Text2),
                    )
                }
            }
        }
    }
}

// =============================================================================
// Action pills — match Figma 8702:137753 (filled) and 8702:137767 (outlined)
// =============================================================================

@Composable
private fun FollowingPill(stillFollowing: Boolean, onToggle: () -> Unit) {
    // Both states render the same outlined→filled pill — only the label
    // changes ("Unfollow" while still following, "Follow" once the user
    // has unfollowed in-session and can re-add).
    val t = LocalAppStrings.current
    SidebarPill(
        label = if (stillFollowing) t.feedUnfollow else t.feedFollow,
        onClick = onToggle,
    )
}

@Composable
private fun RemovePill(deleted: Boolean, onRemove: () -> Unit) {
    val t = LocalAppStrings.current
    if (deleted) {
        // Disabled "Deleted" state after a session-local follower removal —
        // dimmed and non-interactive (no hover state).
        Box(
            modifier = Modifier
                .sHeight(32)
                .sBorderRadius(28)
                .styleModifier {
                    property("padding", "0 ${s(14)}")
                    property("background", "var(--arena-glass-strong)")
                    property("cursor", "default")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                },
        ) {
            SpanText(
                t.feedDeleted,
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(22)
                    .color(com.arena_web.ui.theme.ThemeVars.Text3),
            )
        }
    } else {
        SidebarPill(label = t.feedRemove, onClick = onRemove)
    }
}

/**
 * Sidebar action pill — Figma 8702:137781 (Following) and 8702:137845 (Remove).
 *
 * Default state matches both nodes: 0.5px white-0.4 border, faint blue
 * gradient surface, secondary text. On hover or while pressed the pill
 * flips to the sport primary fill with white text per the user's request.
 * The transition is animated so the state change reads as a deliberate UI
 * cue rather than a flicker.
 */
@Composable
private fun SidebarPill(
    label: String,
    onClick: () -> Unit,
) {
    val sport = LocalSportColors.current
    var hovered by remember { mutableStateOf(false) }
    var pressed by remember { mutableStateOf(false) }
    val active = hovered || pressed

    val primary = sport.primary.toRgb().let { "rgb(${it.red}, ${it.green}, ${it.blue})" }

    Box(
        modifier = Modifier
            .sHeight(32)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .onMouseEnter { hovered = true }
            .onMouseLeave {
                hovered = false
                pressed = false
            }
            .onMouseDown { pressed = true }
            .onMouseUp { pressed = false }
            .styleModifier {
                property("position", "relative")
                property("border-radius", s(28))
                property("padding", "0 ${s(14)}")
                property("background", if (active) primary else ROW_BUTTON_BG)
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("flex-shrink", "0")
                property("transition", "background-color 120ms ease, color 120ms ease")
                property("user-select", "none")
            },
    ) {
        GradientBorder(28)
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(22)
                .sLetterSpacing(0.028)
                .color(if (active) org.jetbrains.compose.web.css.Color(AppColors.PrimaryWhite.toString()) else com.arena_web.ui.theme.ThemeVars.Text2),
        )
    }
}

@Composable
private fun EmptyState(message: String) {
    Box(
        modifier = Modifier.fillMaxWidth().sPaddingTop(16).sPaddingBottom(16),
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            message,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Regular)
                .color(com.arena_web.ui.theme.ThemeVars.Text3),
        )
    }
}

// =============================================================================
// Empty states — Figma 10063:170242
//
// Glyphs come from the shared `TintedGlyph` so every empty-state icon across
// the app picks up the same muted-blue fill (EMPTY_STATE_GLYPH_COLOR).
// =============================================================================

/** Following section empty state — single profile glyph with a "+" badge,
 *  Figma copy ("Not Following Anyone Yet" + descriptor), and a primary blue
 *  "Discover Players" pill with the arrow-up-right glyph in a darker
 *  secondary disc on the right.
 */
@Composable
private fun FollowingEmptyState(onDiscoverClick: () -> Unit) {
    val t = LocalAppStrings.current
    val sport = LocalSportColors.current
    Box(
        modifier = Modifier.fillMaxWidth().sPaddingTop(8).sPaddingBottom(20),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier.sGap(14),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            // Composite glyph: profile + "+" badge nudged into the top-right
            // corner, mirroring Figma 10063:234070+234076 but scaled down to
            // sit closer to the surrounding text without dominating the
            // empty state.
            Box(
                modifier = Modifier.styleModifier {
                    property("position", "relative")
                    property("width", s(58))
                    property("height", s(58))
                }
            ) {
                TintedGlyph(
                    src = AppIcons.Profile,
                    sizePx = 48,
                    modifier = Modifier.styleModifier {
                        property("position", "absolute")
                        property("left", "0")
                        property("bottom", "0")
                    },
                )
                TintedGlyph(
                    src = AppIcons.Add,
                    sizePx = 20,
                    modifier = Modifier.styleModifier {
                        property("position", "absolute")
                        property("right", "0")
                        property("top", "0")
                    },
                )
            }
            SpanText(
                t.feedNotFollowingTitle,
                modifier = Modifier
                    .sFontSize(20)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(26)
                    .sLetterSpacing(0.2)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier { property("text-align", "center") },
            )
            SpanText(
                t.feedNotFollowingBody,
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(20)
                    .sLetterSpacing(0.14)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier {
                        property("text-align", "center")
                        property("max-width", s(360))
                    },
            )
            DiscoverPlayersPill(sport = sport, onClick = onDiscoverClick)
        }
    }
}

/** Followers section empty state — two-people glyph + Figma copy. No action
 *  button: the next step ("share posts and stories") happens elsewhere in
 *  the app, so the empty state stays text-only per Figma 10063:234311.
 */
@Composable
private fun FollowersEmptyState() {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier.fillMaxWidth().sPaddingTop(8).sPaddingBottom(20),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier.sGap(14),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            TintedGlyph(src = AppIcons.People, sizePx = 70)
            SpanText(
                t.feedNoFollowersTitle,
                modifier = Modifier
                    .sFontSize(20)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(26)
                    .sLetterSpacing(0.2)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier { property("text-align", "center") },
            )
            SpanText(
                t.feedNoFollowersBody,
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(20)
                    .sLetterSpacing(0.14)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier {
                        property("text-align", "center")
                        property("max-width", s(360))
                    },
            )
        }
    }
}

@Composable
private fun DiscoverPlayersPill(sport: SportColorScheme, onClick: () -> Unit) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .sHeight(46)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("background", "rgb(${sport.primary.toRgb().red}, ${sport.primary.toRgb().green}, ${sport.primary.toRgb().blue})")
                property("border-radius", s(100))
                property("padding", "${s(8)} ${s(4)} ${s(8)} ${s(20)}")
                property("gap", s(10))
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            t.feedDiscoverPlayers,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(18)
                .sLetterSpacing(0.032)
                .color(sport.onPrimary),
        )
        // 30×30 darker secondary disc with the arrow-up-right glyph.
        Box(
            modifier = Modifier.styleModifier {
                property("width", s(30))
                property("height", s(30))
                property("border-radius", "50%")
                property("background-color", "#004DCD")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
        ) {
            Box(
                modifier = Modifier.styleModifier {
                    property("width", s(12))
                    property("height", s(12))
                    property("background-color", "white")
                    property("-webkit-mask", "url('${AppIcons.ArrowUp}') no-repeat center / contain")
                    property("mask", "url('${AppIcons.ArrowUp}') no-repeat center / contain")
                    property("transform", "rotate(45deg)")
                },
            )
        }
    }
}





